import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Collapsible, CollapsibleContent } from "../ui/collapsible";
import { Trophy, Play, Settings2, ChevronDown, Swords, Sparkles } from "lucide-react";

const FIELD_LABELS = {
  kills: "Kills",
  downs: "Downs",
  damage_dealt: "Damage Dealt",
  damage_received: "Damage Received",
  healing: "Healing Done",
  ending_hp: "Ending HP",
};

function weightedTotal(score, rules) {
  let total = 0;
  Object.entries(rules || {}).forEach(([field, rule]) => {
    if (rule?.enabled) total += (score?.[field] || 0) * (rule.points || 0);
  });
  return Math.round(total * 100) / 100;
}

function branchName(branches, id, round) {
  if (!id) return round === 1 ? "— BYE —" : "TBD — awaiting winner";
  return branches.find((b) => b.id === id)?.name || "TBD";
}

export function TournamentBracketPanel({ raid, raidId, isMasterGm, api, formatApiErrorDetail }) {
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const tournament = raid?.tournament;
  const branches = raid?.branches || [];
  const [rulesDraft, setRulesDraft] = useState(tournament?.scoring_rules || {});

  if (raid?.raid_type !== "tournament") return null;

  const rules = rulesOpen ? rulesDraft : tournament?.scoring_rules || {};

  const generateBracket = async () => {
    setBusy(true);
    try {
      await api.post(`/raids/${raidId}/tournament/bracket`, {});
      toast.success("Bracket generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const startMatch = async (matchId) => {
    try {
      await api.post(`/raids/${raidId}/tournament/matches/${matchId}/start`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const endMatch = async (matchId, winnerBranchId, auto) => {
    try {
      await api.post(`/raids/${raidId}/tournament/matches/${matchId}/end`, { winner_branch_id: winnerBranchId, auto });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveRules = async () => {
    try {
      await api.patch(`/raids/${raidId}/tournament/scoring-rules`, { rules: rulesDraft });
      toast.success("Scoring rules saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openRules = () => {
    setRulesDraft(tournament?.scoring_rules || {});
    setRulesOpen((v) => !v);
  };

  const matches = tournament?.matches || [];
  const rounds = [...new Set(matches.map((m) => m.round))].sort((a, b) => a - b);

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-4 lg:col-span-2" data-testid="raid-tournament-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl flex items-center gap-2"><Swords className="w-4 h-4 text-[#D4AF37]" /> Tournament Bracket</h2>
        {isMasterGm && (
          <button data-testid="tournament-scoring-rules-toggle" onClick={openRules} className="flex items-center gap-1.5 text-xs text-[#f1e0c5]/50 hover:text-[#3B82F6]">
            <Settings2 className="w-3.5 h-3.5" /> Scoring Rules <ChevronDown className={`w-3 h-3 transition-transform ${rulesOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {isMasterGm && (
        <Collapsible open={rulesOpen}>
          <CollapsibleContent className="bg-black/30 rounded-xl p-4 space-y-2" data-testid="tournament-scoring-rules-panel">
            <p className="text-[10px] text-[#f1e0c5]/40">Toggle a stat off to ignore it, or change its point value. Used for the live total preview and "Auto-Decide Winner".</p>
            {Object.keys(FIELD_LABELS).map((field) => {
              const rule = rulesDraft[field] || { enabled: true, points: 0 };
              return (
                <div key={field} className="flex items-center gap-3">
                  <Checkbox
                    data-testid={`tournament-rule-enabled-${field}`}
                    checked={!!rule.enabled}
                    onCheckedChange={(v) => setRulesDraft((d) => ({ ...d, [field]: { ...rule, enabled: v } }))}
                  />
                  <span className="text-sm flex-1">{FIELD_LABELS[field]}</span>
                  <Input
                    data-testid={`tournament-rule-points-${field}`}
                    type="number"
                    step="0.05"
                    value={rule.points}
                    onChange={(e) => setRulesDraft((d) => ({ ...d, [field]: { ...rule, points: parseFloat(e.target.value) || 0 } }))}
                    className="bg-black/30 border-white/10 h-8 w-24 text-sm"
                  />
                  <span className="text-[10px] text-[#f1e0c5]/30 w-16">pts each</span>
                </div>
              );
            })}
            <Button data-testid="tournament-save-rules-button" size="sm" onClick={saveRules} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] mt-2">
              Save Scoring Rules
            </Button>
          </CollapsibleContent>
        </Collapsible>
      )}

      {tournament?.status === "completed" && tournament.champion_branch_id && (
        <div className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl px-4 py-3" data-testid="tournament-champion-banner">
          <Trophy className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-sm">Champion: <strong>{branchName(branches, tournament.champion_branch_id)}</strong></span>
        </div>
      )}

      {matches.length === 0 && (
        <div className="text-center py-6">
          <p className="text-xs text-[#f1e0c5]/30 mb-3">No bracket yet.</p>
          {isMasterGm ? (
            <Button data-testid="tournament-generate-bracket-button" disabled={busy || branches.length < 2} onClick={generateBracket} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
              Generate Bracket
            </Button>
          ) : (
            <p className="text-xs text-[#f1e0c5]/30">Waiting for the Master GM to generate the bracket.</p>
          )}
          {branches.length < 2 && <p className="text-[10px] text-[#f1e0c5]/30 mt-2">Needs at least 2 Branches.</p>}
        </div>
      )}

      {rounds.map((round) => (
        <div key={round} className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-[#f1e0c5]/40">Round {round}</p>
          {matches.filter((m) => m.round === round).map((m) => {
            const scoreA = m.scores?.[m.branch_a_id];
            const scoreB = m.scores?.[m.branch_b_id];
            const totalA = weightedTotal(scoreA, rules);
            const totalB = weightedTotal(scoreB, rules);
            return (
              <div key={m.id} data-testid={`tournament-match-${m.id}`} className="bg-black/20 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className={m.winner_branch_id === m.branch_a_id ? "text-[#D4AF37] font-semibold" : ""}>{branchName(branches, m.branch_a_id, m.round)}{m.winner_branch_id === m.branch_a_id && <Trophy className="inline w-3 h-3 ml-1" />}</span>
                  <span className="text-[10px] uppercase tracking-widest text-[#f1e0c5]/30 px-2 py-0.5 rounded-full bg-black/30">{m.status}</span>
                  <span className={m.winner_branch_id === m.branch_b_id ? "text-[#D4AF37] font-semibold" : ""}>{branchName(branches, m.branch_b_id, m.round)}{m.winner_branch_id === m.branch_b_id && <Trophy className="inline w-3 h-3 ml-1" />}</span>
                </div>

                {m.status === "active" && (
                  <div className="grid grid-cols-2 gap-3 text-[10px] text-[#f1e0c5]/60">
                    {[m.branch_a_id, m.branch_b_id].map((bid, idx) => (
                      <div key={idx} className="space-y-0.5">
                        {Object.keys(FIELD_LABELS).map((f) => (
                          <div key={f} className="flex justify-between"><span>{FIELD_LABELS[f]}</span><span>{m.scores?.[bid]?.[f] ?? 0}</span></div>
                        ))}
                        <div className="flex justify-between font-semibold text-[#3B82F6] pt-0.5 border-t border-white/10"><span>Weighted Total</span><span>{idx === 0 ? totalA : totalB}</span></div>
                      </div>
                    ))}
                  </div>
                )}

                {isMasterGm && m.status === "pending" && m.branch_a_id && m.branch_b_id && (
                  <Button data-testid={`tournament-start-match-${m.id}`} size="sm" onClick={() => startMatch(m.id)} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab] gap-1.5">
                    <Play className="w-3.5 h-3.5" /> Start Match
                  </Button>
                )}

                {isMasterGm && m.status === "active" && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button data-testid={`tournament-declare-a-${m.id}`} size="sm" variant="secondary" className="rounded-full" onClick={() => endMatch(m.id, m.branch_a_id, false)}>
                      Declare Winner: {branchName(branches, m.branch_a_id, m.round)}
                    </Button>
                    <Button data-testid={`tournament-declare-b-${m.id}`} size="sm" variant="secondary" className="rounded-full" onClick={() => endMatch(m.id, m.branch_b_id, false)}>
                      Declare Winner: {branchName(branches, m.branch_b_id, m.round)}
                    </Button>
                    <Button data-testid={`tournament-auto-decide-${m.id}`} size="sm" className="rounded-full bg-[#D4AF37] hover:bg-[#b8952c] text-black gap-1.5" onClick={() => endMatch(m.id, null, true)}>
                      <Sparkles className="w-3.5 h-3.5" /> Auto-Decide Winner
                    </Button>
                  </div>
                )}

                {m.status === "completed" && m.winner_branch_id && (
                  <p className="text-[10px] text-[#f1e0c5]/40">
                    {branchName(branches, m.winner_branch_id === m.branch_a_id ? m.branch_b_id : m.branch_a_id, m.round)} eliminated — can keep playing this map for fun, score no longer counts.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
